import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import useBrandRegistrationVerificationService from '../../app/services/hooks/useBrandRegistrationVerificationService';
import useFlowPaymentService from '../../app/services/hooks/useFlowPaymentService';
import useCurrencyService from '../../app/services/hooks/useCurrencyService';
import useServicePricingService from '../../app/services/hooks/useServicePricingService';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import DefaultCard from '../../components/default/DefaultCard';

const PaymentSummary: React.FC = () => {
    const { verificationId } = useParams<{ verificationId: string }>();
    const history = useHistory();
    const location = useLocation();
    const { getVerificationById } = useBrandRegistrationVerificationService();
    const { createFlowPayment, fetchingCreatePayment, getPaymentStatus, fetchingGetPaymentStatus } = useFlowPaymentService();
    const { getCurrentCurrencyValue, fetchingCurrentValue } = useCurrencyService();
    const { getInapiPrice, fetchingGetInapiPrice } = useServicePricingService();
    const [verificationData, setVerificationData] = useState<any>(null);
    const [brandRegistration, setBrandRegistration] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState<string>('pending');
    const [utmValue, setUtmValue] = useState<number>(0);
    const [utmDate, setUtmDate] = useState<string>('');
    const [basePrice, setBasePrice] = useState<number | null>(null);
    // Desglose calculado por el backend: es exactamente lo que se cobra en Flow
    const [breakdown, setBreakdown] = useState<{
        plan_name: string; service_net: number; iva: number; service_total: number; fees_utm: number;
        utm_value: number; fees_clp: number; total: number; classes: { id: number; fee_utm: number; fee_clp: number }[];
        error?: string;
    } | null>(null);

    // Cargar datos y consultar estado del pago
    useEffect(() => {
        loadVerificationData();
        loadUTMValue();
        loadInapiPrice();
    }, [verificationId]);

    // Consultar estado del pago después de cargar los datos
    useEffect(() => {
        if (verificationData && verificationId) {
            loadPaymentStatus();
        }
    }, [verificationData, verificationId]);

    const loadUTMValue = () => {
        getCurrentCurrencyValue('UTM', {
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.exchange) {
                    setUtmValue(response.data.exchange.value);
                    setUtmDate(response.data.exchange.exchange_date);
                    console.log('Valor UTM cargado:', response.data.exchange.value);
                }
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar valor de UTM:', response.message);
                toast.error('No se pudo cargar el valor de UTM actual');
            }
        });
    };

    const loadInapiPrice = () => {
        getInapiPrice({
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.service_pricing) {
                    const price = response.data.service_pricing.base_price;
                    setBasePrice(price);
                    console.log('Precio INAPI cargado desde mantenedora:', price);
                }
            },
            onError: () => {
                // Sin precio no se muestra un monto inventado: el total viene del backend
                setBasePrice(0);
            }
        });
    };

    const loadVerificationData = async () => {
        try {
            setLoading(true);
            const response: ServiceResponse = await getVerificationById(parseInt(verificationId!));
            
            if (response.data && response.data.verification) {
                console.log('Datos de verificación cargados:', response.data.verification);
                console.log('Clases cargadas:', response.data.verification.classes);
                setVerificationData(response.data.verification);
            } else {
                toast.error('No se pudo cargar la información de la verificación');
                history.push('/intranet/brand-registration');
            }
        } catch (error) {
            console.error('Error loading verification:', error);
            toast.error('Error al cargar la información de la verificación');
            history.push('/intranet/brand-registration');
        } finally {
            setLoading(false);
        }
    };

    const loadPaymentStatus = () => {
        getPaymentStatus(parseInt(verificationId!), {
            onSuccess: (response: ServiceResponse) => {
                if (response.data) {
                    setPaymentStatus(response.data.payment_status);
                    setBreakdown(response.data.breakdown || null);
                    
                    // Guardar datos de la solicitud de marca
                    if (response.data.brand_registration) {
                        setBrandRegistration(response.data.brand_registration);
                    }
                    
                    console.log('Estado del pago actualizado:', response.data.payment_status);
                    console.log('Datos de solicitud:', response.data.brand_registration);
                }
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar el estado del pago:', response.message);
                // Mantener el estado actual si hay error
            }
        });
    };

    const handleBack = () => {
        history.goBack();
    };

    const handleContinuePayment = () => {
        if (!verificationData) return;
        
        setPaymentLoading(true);
        
        createFlowPayment(parseInt(verificationId!), {
            onSuccess: (response) => {
                if (response.data && response.data.payment_url) {
                    // Redirigir a la página de redirección
                    history.push('/intranet/payment/redirect', {
                        paymentUrl: response.data.payment_url,
                        orderId: response.data.order_id
                    });
                } else {
                    toast.error('No se pudo generar el enlace de pago');
                    setPaymentLoading(false);
                }
            },
            onError: (response) => {
                toast.error(response.message || 'Error al crear el pago');
                setPaymentLoading(false);
            }
        });
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    const calculateIVA = (basePrice: number) => {
        return Math.round(basePrice * 0.19); // 19% IVA
    };

    const calculateTotal = (basePrice: number) => {
        return basePrice + calculateIVA(basePrice);
    };

    const handleContinueToInapi = () => {
        if (!verificationData) {
            toast.error('No se encontró la información de la verificación');
            return;
        }
        
        // Redirigir a la página de solicitud INAPI con el verification_id
        history.push(`/intranet/inapi-application?verification_id=${verificationData.id || verificationId}`);
    };

    const handleRetryPayment = () => {
        // Vuelve a mostrar el resumen con el botón de pago (antes navegaba a la misma
        // URL y el componente no cambiaba)
        setPaymentStatus('pending');
    };

    // Renderizar vista según el estado del pago
    const renderPaymentContent = () => {
        if (paymentStatus === 'paid') {
            // Vista de pago exitoso
            return (
                <>
                    {/* Success Header */}
                    <div className="text-center mb-4" style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                        <div className="mb-4">
                            <div 
                                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                style={{ 
                                    width: '100px', 
                                    height: '100px',
                                    background: 'linear-gradient(135deg, rgba(25, 135, 84, 0.1) 0%, rgba(25, 135, 84, 0.2) 100%)',
                                    boxShadow: '0 4px 12px rgba(25, 135, 84, 0.15)'
                                }}
                            >
                            <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        </div>
                        <h4 className="text-success mb-3" style={{ fontWeight: '600', fontSize: '1.75rem', letterSpacing: '-0.02em' }}>
                            ¡Pago Exitoso!
                        </h4>
                        <p className="text-muted mb-0" style={{ fontSize: '1rem', maxWidth: '480px', margin: '0 auto', lineHeight: '1.6' }}>
                            Tu pago ha sido procesado correctamente. Ahora puedes continuar con el proceso de solicitud.
                        </p>
                    </div>

                    {/* Payment Details Card */}
                    <div className="mb-4">
                        <div 
                            style={{ 
                                borderRadius: '0.75rem',
                                border: '1px solid rgba(25, 135, 84, 0.2)',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header Section */}
                            <div 
                                style={{ 
                                    padding: '1.25rem 1.5rem',
                                    backgroundColor: '#d1e7dd',
                                    borderBottom: '1px solid rgba(15, 81, 50, 0.1)'
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-check-circle me-2" style={{ fontSize: '1.15rem', color: '#198754' }}></i>
                                    <h6 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: '600', color: '#0f5132' }}>
                                        Detalles del Pago
                            </h6>
                                </div>
                            </div>
                            
                            {/* Details Section - Horizontal Layout */}
                            <div style={{ padding: '1.5rem' }}>
                                {/* Amount */}
                                <div 
                                    className="d-flex justify-content-between align-items-center" 
                                    style={{ marginBottom: '1.25rem', paddingBottom: '1.25rem', borderBottom: '1px solid rgba(15, 81, 50, 0.1)' }}
                                >
                                    <div>
                                        <div 
                                            style={{ 
                                                fontSize: '0.85rem', 
                                                fontWeight: '500', 
                                                color: '#6c757d',
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Monto Pagado
                                        </div>
                                    </div>
                                    <div>
                                        <div 
                                            className="fw-bold text-end" 
                                            style={{ 
                                                fontSize: '1.5rem', 
                                                color: '#0f5132', 
                                                letterSpacing: '-0.02em',
                                                lineHeight: '1.2'
                                            }}
                                        >
                                            {formatPrice(totalToPay)}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Status */}
                                <div 
                                    className="d-flex justify-content-between align-items-center" 
                                    style={{ marginBottom: verificationData?.payment_date ? '1.25rem' : '0', paddingBottom: verificationData?.payment_date ? '1.25rem' : '0', borderBottom: verificationData?.payment_date ? '1px solid rgba(15, 81, 50, 0.1)' : 'none' }}
                                >
                                    <div>
                                        <div 
                                            style={{ 
                                                fontSize: '0.85rem', 
                                                fontWeight: '500', 
                                                color: '#6c757d',
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            Estado
                                        </div>
                                    </div>
                                    <div>
                                        <span 
                                            className="badge" 
                                            style={{ 
                                                backgroundColor: '#198754',
                                                fontSize: '0.875rem', 
                                                padding: '0.5rem 1rem',
                                                fontWeight: '500',
                                                borderRadius: '0.5rem',
                                                boxShadow: '0 2px 4px rgba(25, 135, 84, 0.2)'
                                            }}
                                        >
                                            <i className="fas fa-check-circle me-1"></i>
                                            Pagado
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Payment Date (if available) */}
                                {verificationData?.payment_date && (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div 
                                                style={{ 
                                                    fontSize: '0.85rem', 
                                                    fontWeight: '500', 
                                                    color: '#6c757d',
                                                    marginBottom: '0.25rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            >
                                                Fecha de Pago
                                            </div>
                                        </div>
                                        <div>
                                            <div 
                                                className="text-end" 
                                                style={{ 
                                                    fontSize: '0.95rem', 
                                                    color: '#0f5132',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                {new Date(verificationData.payment_date).toLocaleDateString('es-CL', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                        </div>
                            </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex gap-3 justify-content-end" style={{ marginTop: '2rem', paddingTop: '0.5rem' }}>
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBack}
                            title="Volver"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleContinueToInapi}
                            title="Continuar con Solicitud INAPI"
                        />
                    </div>
                </>
            );
        } else if (paymentStatus === 'cancelled') {
            // Vista de pago cancelado
            return (
                <>
                    <div className="mb-4 text-center">
                        <div className="mb-3">
                            <i className="fas fa-times-circle text-warning" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h4 className="text-warning">Pago Cancelado</h4>
                        <p className="text-muted">
                            El pago fue cancelado. Puedes intentar nuevamente cuando estés listo.
                        </p>
                    </div>

                    <div className="d-flex gap-3 justify-content-end">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBack}
                            title="Volver"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleRetryPayment}
                            title="Reintentar Pago"
                        />
                    </div>
                </>
            );
        } else if (paymentStatus === 'failed') {
            // Vista de error en el pago
            return (
                <>
                    <div className="mb-4 text-center">
                        <div className="mb-3">
                            <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h4 className="text-danger">Error en el Pago</h4>
                        <p className="text-muted">
                            Ocurrió un error al procesar tu pago. Por favor, intenta nuevamente.
                        </p>
                    </div>

                    <div className="d-flex gap-3 justify-content-end">
                        <ButtonGeneral
                            type="button"
                            variant="secondary"
                            size="medium"
                            onClick={handleBack}
                            title="Volver"
                        />
                        <ButtonGeneral
                            type="button"
                            variant="primary"
                            size="medium"
                            onClick={handleRetryPayment}
                            title="Reintentar Pago"
                        />
                    </div>
                </>
            );
        }
        
        // Vista normal de pago (pending)
        return (
            <>
                {/* Service Details */}
                <div className="mb-4">
                    <div className="bg-light p-3 rounded mb-3">
                        <h5 className="text-primary mb-2">Servicio de registro de marca comercial</h5>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">Solicitud:</span>
                            <span className="fw-bold">
                                #{verificationData.brand_registration?.id || brandRegistration?.id || 'N/A'}
                            </span>
                        </div>
                        {verificationData.brand_registration?.brand_name && (
                            <div className="d-flex justify-content-between align-items-center mt-2">
                                <span className="text-muted">Marca:</span>
                                <span className="fw-bold">{verificationData.brand_registration.brand_name}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Price Breakdown */}
                <div className="mb-4">
                    <h6 className="text-dark mb-3">Desglose de Precios</h6>
                    
                    {/* UTM Value Info */}
                    {utmValue > 0 && (
                        <div className="alert alert-info py-2 mb-3">
                            <small>
                                <i className="fas fa-info-circle me-2"></i>
                                Valor UTM: {formatPrice(utmValue)} ({utmDate ? new Date(utmDate).toLocaleDateString('es-CL') : 'Hoy'})
                            </small>
                        </div>
                    )}
                    
                    {/* Base Service */}
                    <div className="d-flex justify-content-between py-2 border-bottom">
                        <span>Servicio profesional{breakdownOk ? ` (${breakdownOk.plan_name})` : ''}</span>
                        <span className="fw-bold">{formatPrice(serviceNet)}</span>
                    </div>
                    
                    {/* Additional Classes - Detailed */}
                    {verificationData.classes && verificationData.classes.length > 0 && (
                        <>
                            <div className="mt-3 mb-2">
                                <strong className="text-dark">Clases y Tarifas Asociadas:</strong>
                            </div>
                            {verificationData.classes.map((cls: any, index: number) => {
                                const classFeeInCLP = breakdownOk?.classes.find((c) => c.id === cls.id)?.fee_clp
                                    ?? Math.round(cls.fee * utmValue);
                                return (
                                    <div key={index} className="py-2 border-bottom">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1 me-3">
                                                <div className="fw-semibold">
                                                    Clase {cls.order}: {cls.class_name}
                                                </div>
                                                {cls.class_description && (
                                                    <small className="text-muted">{cls.class_description}</small>
                                                )}
                                            </div>
                                            <div className="text-end" style={{ minWidth: '120px' }}>
                                                <div className="fw-bold">{formatPrice(classFeeInCLP)}</div>
                                                <small className="text-muted">
                                                    <span className="badge bg-info bg-opacity-10 text-info">
                                                        {cls.fee} UTM
                                                    </span>
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="d-flex justify-content-between py-2 bg-light">
                                <span className="fw-semibold">Subtotal clases ({verificationData.classes.length})</span>
                                <span className="fw-semibold">{formatPrice(totalFeesInCLP)}</span>
                            </div>
                        </>
                    )}
                    
                    {/* IVA: solo sobre el servicio; las tasas INAPI son fiscales y no llevan IVA */}
                    <div className="d-flex justify-content-between py-2 border-bottom">
                        <span>IVA (19%) del servicio <small className="text-muted">· las tasas INAPI no llevan IVA</small></span>
                        <span>{formatPrice(ivaAmount)}</span>
                    </div>
                    {breakdown?.error && (
                        <div className="alert alert-warning py-2 mt-3 mb-0 small">{breakdown.error}</div>
                    )}
                    
                    {/* Total */}
                    <div className="d-flex justify-content-between p-3 bg-primary bg-opacity-10 rounded mt-3">
                        <span style={{ color: '#FFFFFF !important' }} className="fw-bold">Total a pagar</span>
                        <span style={{ color: '#FFFFFF !important' }} className="fw-bold fs-5">{formatPrice(totalToPay)}</span>
                    </div>
                </div>

                {/* Payment Method Info */}
                <div className="mb-4">
                    <div className="alert alert-info">
                        <div className="d-flex align-items-center">
                            <i className="fas fa-credit-card me-2"></i>
                            <div>
                                <strong>Método de Pago:</strong> Flow Payment
                                <br />
                                <small className="text-muted">
                                    Pago seguro con tarjeta de crédito, débito o transferencia bancaria
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-3 justify-content-end">
                    <ButtonGeneral
                        type="button"
                        variant="secondary"
                        size="medium"
                        onClick={handleBack}
                        title="Volver"
                    />
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        onClick={handleContinuePayment}
                        title="Continuar con el proceso de pago"
                        loading={paymentLoading || fetchingCreatePayment}
                        disabled={paymentLoading || fetchingCreatePayment || !breakdownOk}
                    />
                </div>
            </>
        );
    };

    if (loading || fetchingGetInapiPrice || basePrice === null) {
        return (
            <div className="container-fluid">
                <PageHeader 
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Resumen de Pago', isCurrent: true }
                    ]}
                    title="Resumen de Pago" 
                    description="Cargando información..."
                />
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <DefaultCard>
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                        </DefaultCard>
                    </div>
                </div>
            </div>
        );
    }

    if (!verificationData) {
        return (
            <div className="container-fluid">
                <PageHeader 
                    breadcrumbs={[
                        { label: 'Inicio', path: '/intranet' },
                        { label: 'Error', isCurrent: true }
                    ]}
                    title="Error" 
                    description="No se encontró la información de la verificación"
                />
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <DefaultCard>
                            <div className="text-center py-5">
                                <p className="text-muted">La verificación solicitada no existe o no está disponible.</p>
                                <ButtonGeneral
                                    type="button"
                                    variant="secondary"
                                    size="medium"
                                    onClick={handleBack}
                                    title="Volver"
                                />
                            </div>
                        </DefaultCard>
                    </div>
                </div>
            </div>
        );
    }
    
    // Montos del backend (los mismos que se cobran). Antes se calculaban aquí con el
    // plan por defecto y con IVA también sobre las tasas, y no coincidían con el cobro.
    const breakdownOk = breakdown && !breakdown.error ? breakdown : null;
    const serviceNet = breakdownOk ? breakdownOk.service_net : 0;
    const totalFeesInCLP = breakdownOk ? breakdownOk.fees_clp : 0;
    const ivaAmount = breakdownOk ? breakdownOk.iva : 0;
    const totalToPay = breakdownOk ? breakdownOk.total : 0;

    // Título dinámico según el estado
    const getPageTitle = () => {
        if (paymentStatus === 'paid') return 'Pago Exitoso - Registro de Marca';
        if (paymentStatus === 'cancelled') return 'Pago Cancelado - Registro de Marca';
        if (paymentStatus === 'failed') return 'Error en el Pago - Registro de Marca';
        return 'Resumen de Pago - Registro de Marca';
    };

    return (
        <div className="container-fluid">
            <PageHeader 
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Registro de Marca', path: '/intranet/brand-registration' },
                    { label: paymentStatus === 'paid' ? 'Pago Exitoso' : 'Resumen de Pago', isCurrent: true }
                ]}
                title={getPageTitle()} 
                description={`Solicitud #${brandRegistration?.id || verificationData?.brand_registration?.id || ''}`}
            />
            
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6">
                    <DefaultCard>
                        {renderPaymentContent()}
                    </DefaultCard>
                </div>
            </div>
        </div>
    );
};

export default PaymentSummary;
